/**
 * The editorial layer of the Plugins pages: what a plugin is called for, who
 * made it, which category it files under, and whether its linked account is
 * actually usable.
 *
 * Ported from Amoreg's `customise/_components/plugin-content` so the two
 * surfaces agree on the copy a user sees. Everything here is pure and derived
 * from the connection view — no fetching, so the pages can render it during
 * the first paint.
 *
 * @module components/connections/pluginContent
 */
import type { ConnectionView, ConnectionsCapabilities, SkillView } from "../../state/connections";

/** Built-in skills carry a reserved UUID prefix; they are always available. */
export const BUILT_IN_SKILL_ID_PREFIX = "00000000-0000-4000-8000-";

export function isBuiltInSkill(skillId: string): boolean {
  return skillId.startsWith(BUILT_IN_SKILL_ID_PREFIX);
}

/** Plugins surfaced under "Featured" on the unfiltered view. */
export const FEATURED_PLUGIN_IDS = ["googledrive", "slack", "gmail", "notion"] as const;

const SAMPLE_PROMPTS: Record<string, ReadonlyArray<string>> = {
  googledrive: [
    "Find the latest AML policy in my Drive",
    "Summarise the newest board pack in Google Drive",
    "List the Drive files that changed this week",
  ],
  onedrive: [
    "Find our conflicts-of-interest register in OneDrive",
    "Summarise the latest compliance report in OneDrive",
    "List recently updated files in OneDrive",
  ],
  sharepoint: [
    "Find the onboarding procedure on SharePoint",
    "Summarise the latest policy update on SharePoint",
    "List SharePoint documents tagged for review",
  ],
  gmail: [
    "Draft a reply to the regulator's latest email",
    "Summarise unread emails from the compliance team",
    "Find the email thread about the audit findings",
  ],
  outlook: [
    "Summarise today's unread Outlook emails",
    "Draft a follow-up to the board about the AGM",
    "Find emails mentioning the FCA review",
  ],
  googlecalendar: [
    "What compliance deadlines are on my calendar this month?",
    "Schedule a policy review meeting next week",
    "Summarise tomorrow's meetings",
  ],
  googledocs: [
    "Draft a data-protection policy in Google Docs",
    "Summarise the shared procedures document",
    "Turn my notes into a formal memo",
  ],
  googlesheets: [
    "Build a risk register spreadsheet",
    "Summarise the breach log spreadsheet",
    "Create a training tracker in Sheets",
  ],
  googleslides: [
    "Create a board deck on our regulatory posture",
    "Draft compliance training slides",
    "Summarise this quarter's incidents as a deck",
  ],
  slack: [
    "Summarise what happened in #compliance today",
    "Draft an announcement about the new policy",
    "Find the Slack thread about client onboarding",
  ],
  notion: [
    "Find our incident-response runbook in Notion",
    "Summarise the compliance wiki changes",
    "Draft a new SOP page for client reviews",
  ],
  github: [
    "Summarise open pull requests in our repo",
    "Find issues labelled security",
    "Draft release notes for the latest changes",
  ],
  jira: [
    "List my open Jira tickets",
    "Summarise the remediation project status",
    "Draft a ticket for the audit action items",
  ],
  plaud: [
    "Summarise my latest Plaud recording",
    "Find the meeting where we discussed the compliance review",
    "List the action items from my Plaud recordings this week",
  ],
};

const GENERIC_PROMPTS: ReadonlyArray<(name: string) => string> = [
  (name) => `Search ${name} for the latest compliance updates`,
  (name) => `Summarise recent activity in ${name}`,
  (name) => `Find documents in ${name} related to client onboarding`,
];

export function getPluginPrompts(
  connection: Pick<ConnectionView, "id" | "name">,
): ReadonlyArray<string> {
  return SAMPLE_PROMPTS[connection.id] ?? GENERIC_PROMPTS.map((build) => build(connection.name));
}

const readString = (metadata: ConnectionView["metadata"], key: string): string | null => {
  const value = metadata?.[key];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
};

export function getPluginDeveloper(
  connection: Pick<ConnectionView, "metadata" | "source">,
): string {
  const developer = readString(connection.metadata, "developer");
  if (developer !== null) {
    return developer;
  }
  if (connection.metadata?.marketplaceSource === "curated" || connection.source === "native") {
    return "Amoreg";
  }
  return "Composio";
}

/** Short tagline for headers — the first sentence of the description. */
export function getPluginTagline(connection: Pick<ConnectionView, "description" | "name">): string {
  const firstSentence = connection.description.split(/(?<=[.!?])\s/)[0] ?? "";
  const tagline = firstSentence.replace(/[.!?]+$/, "").trim();
  return tagline || `Work with ${connection.name} from Amoreg`;
}

export function getPluginPrimaryCategory(connection: Pick<ConnectionView, "metadata">): string {
  const categories = connection.metadata?.categories;
  if (Array.isArray(categories) && typeof categories[0] === "string") {
    return categories[0];
  }
  return readString(connection.metadata, "category") ?? "Other";
}

/** Human label for the linked account (e.g. the Google address), if known. */
export function getConnectedAccountLabel(
  connection: Pick<ConnectionView, "userMetadata">,
): string | null {
  const label = connection.userMetadata?.accountLabel;
  return typeof label === "string" && label.trim().length > 0 ? label.trim() : null;
}

/**
 * Connections made before the auth configs carried tool scopes only granted
 * identity scopes — detectable because the provider's granted-scope string
 * lacks the product scope. Reconnecting through the scoped auth config fixes
 * them. Only patterns we can judge reliably (Google) are listed.
 */
const REQUIRED_SCOPE_PRODUCTS: Record<string, ReadonlyArray<string>> = {
  googledrive: ["drive", "docs"],
  googledocs: ["documents", "drive", "docs"],
  googlesheets: ["spreadsheets", "drive"],
  googleslides: ["presentations", "drive"],
  googlecalendar: ["calendar"],
  gmail: ["gmail"],
};

function isProductScope(scope: string, product: string): boolean {
  const base = `https://www.googleapis.com/auth/${product}`;
  if (scope === base) return product !== "gmail";
  const suffix = scope.slice(base.length + 1);
  return scope.startsWith(`${base}.`) && /^[a-z_]+(?:\.[a-z_]+)*$/i.test(suffix);
}

export function connectionNeedsRescope(
  connection: Pick<ConnectionView, "id" | "userMetadata" | "connectionStatus">,
): boolean {
  if (connection.connectionStatus !== "connected") {
    return false;
  }
  const scopes = connection.userMetadata?.grantedScopes;
  if (typeof scopes !== "string" || scopes.trim().length === 0) {
    return false;
  }
  const products = REQUIRED_SCOPE_PRODUCTS[connection.id];
  if (!products) return false;
  return !scopes.trim().split(/\s+/).some((scope) =>
    // Composio's managed Gmail consent can use Google's full-mail scope.
    (connection.id === "gmail" && scope === "https://mail.google.com/") ||
    products.some((product) => isProductScope(scope, product)),
  );
}

/** True when the plugin's account can be linked by this server's broker. */
export function supportsConnect(connection: Pick<ConnectionView, "connectable">): boolean {
  return connection.connectable === true;
}

/** One account lifecycle drives both the catalogue and detail page. */
export function getPluginAccountState(
  connection: ConnectionView,
  capabilities: Pick<ConnectionsCapabilities, "connect" | "disconnect">,
) {
  const connected = connection.connectionStatus === "connected";
  const pending = connection.connectionStatus === "pending";
  const limitedAccess = connectionNeedsRescope(connection);
  const needsReconnect = connection.connectionStatus === "error";
  const canConnect = supportsConnect(connection) && capabilities.connect && !connected && !pending;
  const canDisconnect =
    supportsConnect(connection) && capabilities.disconnect && (connected || pending);
  // Access warnings do not undo a verified account connection. In particular,
  // never offer Connect (which can replace the grant) for an existing account.
  const ready = connected && connection.userEnabled && !limitedAccess;
  const failure =
    connection.connectionStatus === "error" ? connection.userMetadata?.connectionError : null;
  const failureDescription =
    failure === "verification_unavailable"
      ? "Sign-in finished, but Amoreg could not verify your account. Account connections need an administrator’s attention."
      : failure === "sign_in_failed"
        ? "Sign-in did not finish. Connect to try again."
        : failure === "sign_in_expired"
          ? "The connection was not confirmed in time. Connect to try again."
          : null;
  const label = connected
    ? "Connected"
    : pending
      ? "Connecting"
      : needsReconnect
        ? "Needs attention"
        : canConnect
          ? "Not connected"
          : "Connection unavailable";
  const description = connected
    ? !connection.userEnabled
      ? `Your ${connection.name} account is connected, but access is disabled. Disconnect before connecting again to restore access.`
      : limitedAccess
        ? `Your ${connection.name} account is connected with limited permissions. Disconnect before connecting again to grant additional access.`
        : `Your ${connection.name} account is connected. Disconnect to remove Amoreg’s access.`
    : pending
      ? `Complete sign-in in the other tab. Amoreg will confirm your ${connection.name} connection here.`
      : needsReconnect
        ? (failureDescription ?? `Reconnect your ${connection.name} account to restore access.`)
        : canConnect
          ? `Connect your ${connection.name} account to give Amoreg access to your tools and content.`
          : connection.source === "composio" && connection.toolkitSlug
            ? "Account connections are currently unavailable. Try again later."
            : `Connecting a ${connection.name} account is not supported here yet.`;
  return {
    connected,
    ready,
    pending,
    needsReconnect,
    canConnect,
    canDisconnect,
    label,
    description,
  };
}

/** Built-in skill slugs that pair naturally with a plugin. */
const PLUGIN_SKILL_SLUGS: Record<string, ReadonlyArray<string>> = {
  googledocs: [
    "create-docx-files",
    "edit-existing-docx-files",
    "generate-regulatory-docx-documents",
  ],
  googledrive: ["create-docx-files", "create-google-sheets-files"],
  googlesheets: ["create-google-sheets-files"],
  googleslides: ["create-pptx-presentation-files"],
  onedrive: ["create-docx-files", "edit-existing-docx-files"],
  sharepoint: ["generate-regulatory-docx-documents"],
};

export function getRelatedSkills(
  connection: Pick<ConnectionView, "id">,
  skills: ReadonlyArray<SkillView>,
): ReadonlyArray<SkillView> {
  const slugs = PLUGIN_SKILL_SLUGS[connection.id];
  return slugs ? skills.filter((skill) => slugs.includes(skill.slug)) : [];
}

/**
 * Every plugin on this page is a personal connection made by the signed-in
 * user. Organisation-wide connectors are administered from the compliance
 * workspace, so this filter follows account state, never the legacy collection.
 */
export type PluginSourceFilter = "all" | "connected";

export function filterPlugins(
  connections: ReadonlyArray<ConnectionView>,
  filter: PluginSourceFilter,
  query = "",
): ReadonlyArray<ConnectionView> {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  return connections.filter(
    (connection) =>
      (filter === "all" || connection.connectionStatus === "connected") &&
      (!normalizedQuery || matchesPluginQuery(connection, normalizedQuery)),
  );
}

/** Free-text match across the fields a user would plausibly type. */
export function matchesPluginQuery(connection: ConnectionView, query: string): boolean {
  const categories = connection.metadata?.categories;
  const categoryValues = Array.isArray(categories) ? categories : [];
  return [
    connection.name,
    connection.description,
    connection.toolkitSlug,
    connection.connectionStatus === "connected" ? getConnectedAccountLabel(connection) : null,
    ...connection.capabilities,
    ...categoryValues,
    connection.metadata?.category,
    connection.metadata?.developer,
  ]
    .filter((value): value is string => typeof value === "string")
    .join(" ")
    .toLocaleLowerCase()
    .includes(query);
}

/** Group the catalogue by primary category, alphabetically. */
export function groupPluginsByCategory(
  connections: ReadonlyArray<ConnectionView>,
): ReadonlyArray<readonly [string, ReadonlyArray<ConnectionView>]> {
  const groups = new Map<string, ConnectionView[]>();
  for (const connection of connections) {
    const category = getPluginPrimaryCategory(connection);
    const group = groups.get(category) ?? [];
    group.push(connection);
    groups.set(category, group);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/** Each plugin has one place in the unfiltered catalogue. */
export function partitionPluginCatalogue(connections: ReadonlyArray<ConnectionView>) {
  const accounts: ConnectionView[] = [];
  const featured: ConnectionView[] = [];
  const library: ConnectionView[] = [];
  const featuredIds = new Set<string>(FEATURED_PLUGIN_IDS);
  for (const connection of connections) {
    if (connection.connectionStatus !== "disconnected") accounts.push(connection);
    else if (featuredIds.has(connection.id)) featured.push(connection);
    else library.push(connection);
  }
  return { accounts, featured, categories: groupPluginsByCategory(library) };
}
